import React, { useEffect, useMemo, useState } from 'react';

import { LeadCard } from 'models/Lead';

interface UseLeadBulkSelectionArgs {
  /** Rows of the loaded page. */
  rows: LeadCard[];
  /** APPLIED country (never the popover draft): the bulk modal's catalog. */
  country: string;
  /** Changes whenever the page, sort, page size, search or filters change. */
  resetKey: string;
  refetch: () => void;
}

/**
 * Bulk selection of the grid: per page, by lead id. The bar, the modal title,
 * the button label and the payload all read `selectedLeads`, so they agree.
 */
export function useLeadBulkSelection({
  rows,
  country,
  resetKey,
  refetch,
}: UseLeadBulkSelectionArgs) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // The selection belongs to one page/filter combination.
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [resetKey]);

  // Ids that left the loaded rows (refetch, edit) cannot stay selected.
  useEffect(() => {
    setSelectedRowKeys((prev) => {
      const next = prev.filter((key) => rows.some((r) => r.id === key));
      return next.length === prev.length ? prev : next;
    });
  }, [rows]);

  const selectedLeads = useMemo(
    () => rows.filter((l) => selectedRowKeys.includes(l.id)),
    [rows, selectedRowKeys]
  );

  const handleBulkSuccess = () => {
    setSelectedRowKeys([]);
    refetch();
  };

  return {
    selectedRowKeys,
    setSelectedRowKeys,
    selectedLeads,
    count: selectedLeads.length,
    handleBulkSuccess,
    modalProps: { country, leads: selectedLeads, onSuccess: handleBulkSuccess },
  };
}
